import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';
import {
  APP_RELAYS,
  parseBlossomServersFromTags,
  parseNip65RelaysFromTags,
  type RelayEntry,
} from '@/lib/appRelays';
import { newestOfKind, rawRelayQuery } from '@/lib/rawRelayQuery';

export interface FetchedNetworkLists {
  relays: RelayEntry[];
  relayEvent?: NostrEvent;
  blossomServers: string[];
  blossomEvent?: NostrEvent;
  /** Debug: which hop produced blossom (for status UI) */
  blossomSource?: string;
}

type QueryFn = (
  filters: NostrFilter[],
  opts?: { signal?: AbortSignal },
) => Promise<NostrEvent[]>;

type GroupFn = (urls: string[]) => { query: QueryFn } | undefined;

/**
 * Relays known to index / host replaceable user lists (NIP-65 / BUD-03).
 * Queried with raw WebSockets so we don't depend on NPool EOSE races.
 */
export const LIST_DISCOVERY_RELAYS = [
  'wss://relay.ditto.pub',
  'wss://purplepag.es',
  'wss://relay.nostr.band',
  'wss://user.kind.social',
  'wss://relay.primal.net',
  'wss://relay.damus.io',
  'wss://nos.lol',
  'wss://relay.snort.social',
  ...APP_RELAYS.relays.map((r) => r.url),
];

function newerEvent(
  a: NostrEvent | undefined,
  b: NostrEvent | undefined,
): NostrEvent | undefined {
  if (!a) return b;
  if (!b) return a;
  return b.created_at > a.created_at ? b : a;
}

/**
 * Fetch a single replaceable kind via raw WebSockets to many relays.
 * This is the reliable path for kind 10063 (Blossom) which NPool often misses.
 */
async function rawFetchKind(
  kind: number,
  pubkey: string,
  urls: string[],
  signal?: AbortSignal,
  timeoutMs = 12_000,
): Promise<NostrEvent | undefined> {
  const events = await rawRelayQuery(
    urls,
    [{ kinds: [kind], authors: [pubkey], limit: 3 }],
    { timeoutMs, signal },
  );
  return newestOfKind(events, kind);
}

/**
 * Fetch NIP-65 (10002) and Blossom (10063) for a pubkey.
 *
 * Primary path: raw browser WebSockets to discovery + user relays.
 * NPool/batcher is only a secondary hint — it races EOSE and often drops 10063.
 */
export async function fetchUserNetworkLists(
  opts: {
    pubkey: string;
    query: QueryFn;
    /** Optional: open a temporary pool against specific relay URLs */
    group?: GroupFn;
    /** Extra relay URLs already in the app config pool */
    configuredRelayUrls?: string[];
    signal?: AbortSignal;
  },
): Promise<FetchedNetworkLists> {
  const { pubkey, query, configuredRelayUrls = [], signal } = opts;

  // ── Hop 1: raw WS to discovery relays (independent of NPool) ──────────
  const discoveryUrls = [
    ...new Set([...LIST_DISCOVERY_RELAYS, ...configuredRelayUrls]),
  ];

  const [relayRaw1, blossomRaw1] = await Promise.all([
    rawFetchKind(10002, pubkey, discoveryUrls, signal, 12_000),
    rawFetchKind(10063, pubkey, discoveryUrls, signal, 12_000),
  ]);

  let relayEvent = relayRaw1;
  let blossomEvent = blossomRaw1;
  let blossomSource = blossomRaw1 ? 'discovery-raw' : undefined;

  // ── Hop 2: raw WS to user's NIP-65 relays ─────────────────────────────
  if (relayEvent) {
    const entries = parseNip65RelaysFromTags(relayEvent.tags);
    const userUrls = [
      ...new Set(
        entries
          .filter((r) => r.read || r.write)
          .map((r) => r.url.trim())
          .filter(Boolean),
      ),
    ].slice(0, 16);

    if (userUrls.length > 0) {
      const [relay2, blossom2] = await Promise.all([
        rawFetchKind(10002, pubkey, userUrls, signal, 12_000),
        rawFetchKind(10063, pubkey, userUrls, signal, 12_000),
      ]);
      relayEvent = newerEvent(relayEvent, relay2);
      if (blossom2 && (!blossomEvent || blossom2.created_at > blossomEvent.created_at)) {
        blossomEvent = blossom2;
        blossomSource = 'nip65-raw';
      }
    }
  }

  // ── Hop 3: fallback NPool query (may still help on some setups) ───────
  if (!blossomEvent || !relayEvent) {
    try {
      // No limit → skip NostrBatcher replaceable merge
      const [poolRelay, poolBlossom] = await Promise.all([
        !relayEvent
          ? query(
              [{ kinds: [10002], authors: [pubkey] }],
              { signal },
            ).then((evs) => newestOfKind(evs, 10002))
          : Promise.resolve(undefined),
        !blossomEvent
          ? query(
              [{ kinds: [10063], authors: [pubkey] }],
              { signal },
            ).then((evs) => newestOfKind(evs, 10063))
          : Promise.resolve(undefined),
      ]);
      if (poolRelay) relayEvent = newerEvent(relayEvent, poolRelay);
      if (poolBlossom) {
        blossomEvent = newerEvent(blossomEvent, poolBlossom);
        blossomSource = blossomSource ?? 'pool';
      }
    } catch {
      /* pool fallback best-effort */
    }
  }

  const blossomServers = blossomEvent
    ? parseBlossomServersFromTags(blossomEvent.tags)
    : [];

  return {
    relays: relayEvent ? parseNip65RelaysFromTags(relayEvent.tags) : [],
    relayEvent,
    blossomServers,
    blossomEvent,
    blossomSource: blossomServers.length
      ? blossomSource
      : blossomEvent
        ? 'parse-empty'
        : undefined,
  };
}
